/**
 * @fileoverview PayDayPuffin Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/payday-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 *
 * Includes the payday-specific Payroll Services + Subprocessors
 * sections (private-label remediation, Apr 11–12, 2026).
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'payday',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PayDayPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-11',
    contactEmail: 'hello@paydaypuffin.com',
    canonicalUrl: 'https://paydaypuffin.com/terms',
    ogImage: 'https://paydaypuffin.com/og-image.jpg',
    governingLaw: 'Oregon',
    description:
      'PayDayPuffin terms of service. Covers usage of our payroll software services, user responsibilities, subprocessor disclosure, and liability.',
  },
  sections: [
    {
      id: 'acceptance-of-terms',
      title: '1. Acceptance of Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName} ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is a payroll software platform for small businesses. We provide timecard-to-payroll automation, payroll workflow management, employee enrollment, audit logging, and reporting — all accessed through a single web application and unified subscription. To execute the components of payroll that require licensing (wage payments, tax filings, direct deposit), we engage authorized subprocessors. See Section 5 for our current subprocessors.',
        },
      ],
    },
    {
      id: 'user-responsibilities',
      title: '3. User Responsibilities',
      order: 3,
      blocks: [
        { type: 'paragraph', text: 'As a user of the Service, you agree to:' },
        {
          type: 'list',
          items: [
            'Provide accurate and complete information when using the Service',
            'Review payroll submissions before final approval',
            'Maintain the security of your account credentials',
            'Comply with all applicable federal, state, and local payroll and employment laws',
            'Not use the Service for any unlawful purpose',
          ],
        },
      ],
    },
    {
      id: 'payroll-services',
      title: '4. Payroll Services',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides payroll software services including timecard automation, payroll workflow management, employee enrollment, audit logging, and reporting. Payroll execution — including wage payments, tax withholding and filings, direct deposit, and government reporting — is performed on your behalf by Gusto, Inc., a licensed payroll processor and our authorized subprocessor. You do not need a separate account with Gusto; your {productName} subscription covers both the software and the payroll processing services. A complete list of the subprocessors we use is available at paydaypuffin.com/subprocessors.',
        },
        {
          type: 'paragraph',
          text: 'You are responsible for reviewing and approving each payroll run before submission and for verifying the accuracy of all timecard and employee data. You are responsible for compliance with all applicable federal, state, and local payroll and employment laws as the employer of record for your business.',
        },
      ],
    },
    {
      id: 'subprocessors',
      title: '5. Subprocessors',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} engages third-party service providers (subprocessors) to deliver certain components of the Service under contractual obligations that protect the confidentiality, integrity, and availability of your data.',
        },
        {
          type: 'paragraph',
          text: 'As of the effective date of these Terms, our subprocessors include:',
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Gusto, Inc.',
              text: '(Delaware C-corporation) — Executes payroll on your behalf, including wage disbursement, tax filing, direct deposit, W-2 and 1099 generation, and pay stub issuance.',
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'A complete and current list of our subprocessors is maintained at paydaypuffin.com/subprocessors.',
        },
        {
          type: 'paragraph',
          text: "We will provide at least thirty (30) days' notice before engaging any new subprocessor, via posting to our subprocessors page and email notification to the primary account holder. You may object to a new subprocessor on reasonable, objective, and substantive grounds by contacting {contactEmail} within the notice period. If we cannot reasonably accommodate your objection, you may terminate your subscription without penalty.",
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '6. Limitation of Liability',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'To the maximum extent permitted by law, {productName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to errors in payroll calculations, missed payroll deadlines, or data loss.',
        },
      ],
    },
    {
      id: 'intellectual-property',
      title: '7. Intellectual Property',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'All content, features, and functionality of the Service are owned by {productName} and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.',
        },
      ],
    },
    {
      id: 'termination',
      title: '8. Termination',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may terminate or suspend your access to the Service at any time, without prior notice or liability, for any reason. Upon termination, your right to use the Service will immediately cease.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '9. Changes to Terms',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these Terms of Service at any time. We will provide notice of material changes by posting the updated terms on this page. Your continued use of the Service after changes constitutes acceptance of the new terms.',
        },
      ],
    },
    {
      id: 'governing-law',
      title: '10. Governing Law',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'These Terms of Service shall be governed by and construed in accordance with the laws of the State of {governingLaw}, without regard to its conflict of law provisions.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '11. Contact Us',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
